import { Config, DestroyReason, DriveStep, Driver, driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useCallback, useEffect, useRef, useState } from "react";

import { TOUR_SCROLL_MARGIN_BOTTOM, TOUR_SCROLL_MARGIN_TOP } from "./steps";

export type { DriveStep, DriverHook, HookOpts } from "driver.js";

/**
 * CLE-3015 — the thin driver.js layer under `useTour`. The only module (with useTour's types) that
 * imports driver.js. Born as the PR 0 spike's hook; the spike's tests moved with it.
 *
 * - `allowClose: false` + `overlayClickBehavior: "none"`: Escape and overlay clicks never end the tour;
 *   only the popover's own (React) Exit does, via `destroy`.
 * - `onPopoverRender` puts an empty element in the popover's description (replacing driver's placeholder
 *   text when the step has no description of its own) and hands it back as
 *   `popoverContainer`, so the caller can `createPortal` React content into it. driver.js builds a new
 *   popover for every step, so the container changes per step and the old portal unmounts.
 * - The driver instance is destroyed on unmount, which also cancels a pending `waitForElement` (driver
 *   clears its timeout and disconnects its MutationObserver).
 * - `hooks` are passed through to driver.js (read through a ref, so inline callbacks do not restart it).
 *   With `onNextClick` set, both the Next button and an `advanceOnClick` click call it instead of moving
 *   on; with `onDestroyStarted` set, driver.js calls it instead of tearing down — including when it runs
 *   off the end of the steps because every remaining target was missing, the one case `onDestroyed`
 *   never reports — and the hook must call `destroy()` itself.
 * - `runKey` restarts the driver with the current `steps` / `startIndex` while `enabled` stays true.
 * - Placement (manual UX pass of 2026-10-26): the highlight and the popover are re-placed whenever the
 *   highlighted element or the popover changes size (`onHighlighted`); a step whose `data.scrollBlock` is
 *   "end" is scrolled to the bottom of the screen before driver.js scrolls (`scrollToStepEnd`).
 */
export type IDriverHooks = Pick<
  Config,
  "onHighlightStarted" | "onNextClick" | "onDestroyStarted"
>;

/**
 * driver.js's `stagePadding` (its default is 10): the highlight's margin around the element. 10 px reached
 * into the neighbours, cutting through the "First name" labels under Order Type and taking in the "1" of
 * "Message 1" beside View message (manual UX pass of 2026-10-26). The popover sits this plus driver's
 * `popoverOffset` (10) away from the element.
 */
export const STAGE_PADDING = 6;

/**
 * `scrollBlock: "end"` (the follow-ups, ITourStep): runs before driver.js scrolls (`onHighlightStarted`). When the
 * element is not fully on screen it goes to the bottom of the viewport, so what sits above it (the fields the
 * click before opened) stays in view; driver.js then sees it on screen and leaves the scroll alone. An element
 * with no box (jsdom, a hidden one) is left to driver.js.
 */
export function scrollToStepEnd(element: Element | undefined): void {
  if (!element || typeof element.scrollIntoView !== "function") return;
  const rect = element.getBoundingClientRect();
  if (!rect.width && !rect.height) return;
  const fits =
    rect.top >= TOUR_SCROLL_MARGIN_TOP &&
    rect.bottom <= window.innerHeight - TOUR_SCROLL_MARGIN_BOTTOM;
  if (fits) return;
  // The anchor's scroll-margin-bottom (TourPopoverGlobalStyle) leaves TOUR_SCROLL_MARGIN_BOTTOM under it.
  element.scrollIntoView({ block: "end" });
}

export interface IUseDriverTourOptions {
  steps: DriveStep[];
  enabled: boolean;
  startIndex?: number;
  runKey?: number | string;
  onDestroyed?: (reason: DestroyReason | undefined) => void;
  hooks?: IDriverHooks;
  popoverClass?: string;
}

export interface IUseDriverTourResult {
  popoverContainer: HTMLElement | null;
  destroy: () => void;
  moveTo: (index: number) => void;
  getActiveIndex: () => number | undefined;
}

export function useDriverTour({
  steps,
  enabled,
  startIndex = 0,
  runKey,
  onDestroyed,
  hooks,
  popoverClass,
}: IUseDriverTourOptions): IUseDriverTourResult {
  const [popoverContainer, setPopoverContainer] = useState<HTMLElement | null>(
    null
  );
  const driverRef = useRef<Driver | null>(null);
  // Read through refs so a caller passing inline arrays/callbacks does not restart the tour.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const startIndexRef = useRef(startIndex);
  startIndexRef.current = startIndex;
  const onDestroyedRef = useRef(onDestroyed);
  onDestroyedRef.current = onDestroyed;
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  /** Watches the highlighted element and the popover for size changes (see `onHighlighted`). */
  const sizeObserverRef = useRef<ResizeObserver | null>(null);
  const stopWatchingSizes = () => {
    sizeObserverRef.current?.disconnect();
    sizeObserverRef.current = null;
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const hookConfig: Partial<Config> = {
      onHighlightStarted: (element, step, opts) => {
        // A new step: stop re-placing the old one (a refresh mid-transition measures the previous element).
        stopWatchingSizes();
        if (step.data?.scrollBlock === "end") scrollToStepEnd(element);
        hooksRef.current?.onHighlightStarted?.(element, step, opts);
      },
    };
    if (hooksRef.current?.onNextClick) {
      hookConfig.onNextClick = (...args) =>
        hooksRef.current?.onNextClick?.(...args);
    }
    if (hooksRef.current?.onDestroyStarted) {
      hookConfig.onDestroyStarted = (...args) =>
        hooksRef.current?.onDestroyStarted?.(...args);
    }

    const instance = driver({
      steps: stepsRef.current,
      allowClose: false,
      overlayClickBehavior: "none",
      stagePadding: STAGE_PADDING,
      ...(popoverClass ? { popoverClass } : {}),
      ...hookConfig,
      onPopoverRender: (popover, { state }) => {
        const container = document.createElement("div");
        container.className = "tour-react-content";
        // driver.js builds the description and title with placeholder text ("Popover description is
        // here", "Popover Title") and overwrites them only when the step has text of its own. The
        // engine's steps never do (the content is React), so the placeholders were showing above our
        // content (dev feedback round 1). Replace them; keep text a step did set.
        const stepPopover = state.activeStep?.popover;
        if (stepPopover?.description) {
          popover.description.appendChild(container);
        } else {
          popover.description.replaceChildren(container);
        }
        if (!stepPopover?.title) popover.title.replaceChildren();
        // Shown even when a step has no description text of its own.
        popover.description.style.display = "block";
        setPopoverContainer(container);
      },
      // driver.js sizes and places the popover inside `onPopoverRender`, before React has
      // committed the caller's portal content into it. A `side: "bottom"` popover is anchored by
      // `style.bottom`, so that late content grows it UPWARD over the highlighted element (Dev QA
      // of #1432), and driver only repositions on resize/scroll. Re-place it once the highlight
      // transition ends (`duration`, 400 ms), by which time the portal has long committed.
      // `refresh()` is a no-op before then: it reads driver's `__activeElement`, set only at the end.
      // Driver-level, so it runs for every step of every segment the engine drives (CLE-3015 PR 2
      // carries #1434's fix forward into the moved hook).
      //
      // From then on, any size change of the highlighted element or of the popover re-places both
      // (manual UX pass of 2026-10-26): a step that asks the trainee to open something inside the element
      // (the Estimate breakdown on 2.12) grows it, and a card form that loads late (4.6) does too, and the
      // highlight stayed at the old size; the popover lost its arrow when its "fill in the fields" line went
      // away (2.7). driver.js only re-places on window resize and scroll.
      onHighlighted: (element, _step, { state }) => {
        instance.refresh();
        stopWatchingSizes();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => {
          if (element && !element.isConnected) return;
          instance.refresh();
        });
        [element, state.popover?.wrapper].forEach((watched) => {
          if (watched) observer.observe(watched);
        });
        sizeObserverRef.current = observer;
      },
      onDestroyed: (_element, _step, opts) => {
        stopWatchingSizes();
        setPopoverContainer(null);
        onDestroyedRef.current?.(opts.reason);
      },
    });
    driverRef.current = instance;
    instance.drive(startIndexRef.current);

    return () => {
      stopWatchingSizes();
      instance.destroy();
      if (driverRef.current === instance) driverRef.current = null;
      setPopoverContainer(null);
    };
  }, [enabled, runKey, popoverClass]);

  const destroy = useCallback(() => {
    stopWatchingSizes();
    driverRef.current?.destroy();
    setPopoverContainer(null);
  }, []);

  const moveTo = useCallback((index: number) => {
    driverRef.current?.moveTo(index);
  }, []);

  const getActiveIndex = useCallback(
    () => driverRef.current?.getActiveIndex(),
    []
  );

  return { popoverContainer, destroy, moveTo, getActiveIndex };
}
